import { Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { DraftAssociation } from '@core/filters/path-filter/draft';
import { Quantifier, edgePath } from '@core/filters/path-filter/wire';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';

import { AvailableLanguages } from '@shared/types/translation.type';

import { PATH_FILTER_COMPOSER_STATE } from '../composer/path-filter-composer-state';
import { GROUP_MODES, GroupMode, groupModeOf } from '../group-mode';
import { scopedAssociationLabel } from '../path-filter-labels';
import { ComposerContext } from '../path-filter.models';

export type BlockMode = 'all' | 'any' | 'no';

/** No is a negated `any`: not one related row matches. */
const BLOCK_MODES: Record<BlockMode, Pick<DraftAssociation, 'quantifier' | 'negated'>> = {
  all: { quantifier: 'all', negated: false },
  any: { quantifier: 'any', negated: false },
  no: { quantifier: 'any', negated: true }
};

/** Stands in for the noun while the header's translation is split around it. */
const NOUN_MARK = '\u0000';

/** The block's header; the group it filters on is projected, which keeps this file free of the recursion. */
@Component({
  selector: 'app-path-filter-association',
  imports: [TranslatePipe],
  templateUrl: './path-filter-association.component.html',
  styles: `
    :host {
      display: block;
    }
  `
})
export class PathFilterAssociationComponent {
  association = input.required<DraftAssociation>();
  /** Edge path of the filter holding this block, null at the root. */
  scope = input<string | null>(null);
  context = input.required<ComposerContext>();

  associationChange = output<DraftAssociation>();
  removed = output<void>();

  protected readonly ICONS = ICONS;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly hovered = inject(PATH_FILTER_COMPOSER_STATE).hovered;
  protected readonly linked = computed(() => this.hovered() === this.association().id);

  protected readonly meta = computed(() =>
    this.context().vocabulary.association(edgePath(this.scope(), this.association().edge))
  );
  protected readonly label = computed(() =>
    scopedAssociationLabel(
      this.context().vocabulary,
      this.scope(),
      this.association().edge,
      this.currentLang() as AvailableLanguages,
      this.translate
    )
  );
  private readonly quantifiers = computed<Quantifier[]>(
    () => this.meta()?.quantifiers ?? ['any', 'all']
  );

  protected readonly modes = computed(() =>
    (Object.keys(BLOCK_MODES) as BlockMode[]).filter(
      (mode) => mode !== 'all' || this.quantifiers().includes('all')
    )
  );
  protected readonly mode = computed<BlockMode>(() =>
    this.association().negated ? 'no' : this.association().quantifier
  );

  /** The served title is plural, lower-cased mid-sentence and set apart from the words around it. */
  protected readonly phrase = computed(() => {
    const lang = this.currentLang() as AvailableLanguages;
    const text: string = this.translate.instant(
      'features.data.path_filter.association.where',
      { noun: NOUN_MARK }
    );
    const [before, after = ''] = text.split(NOUN_MARK);
    return { before, noun: this.label().toLocaleLowerCase(lang), after };
  });

  protected readonly bodyModes = Object.keys(GROUP_MODES) as GroupMode[];
  protected readonly bodyMode = computed(() => groupModeOf(this.association().filter));
  /** The body's mode only matters once there is something to connect, unless it negates the body. */
  protected readonly showsBodyMode = computed(() => {
    const body = this.association().filter;
    return body.children.length > 1 || body.negated;
  });
  /** Lower-cased: the body's mode reads mid-sentence, after the block's own phrase. */
  protected readonly bodyModeLabels = computed(() => {
    const lang = this.currentLang() as AvailableLanguages;
    return Object.fromEntries(
      this.bodyModes.map((mode) => [
        mode,
        (
          this.translate.instant(`features.data.path_filter.group.mode.${mode}`) as string
        ).toLocaleLowerCase(lang)
      ])
    ) as Record<GroupMode, string>;
  });
  protected readonly negatesSomething = computed(
    () => this.association().negated || this.association().filter.negated
  );

  protected setMode(mode: BlockMode): void {
    if (mode !== this.mode()) {
      this.associationChange.emit({ ...this.association(), ...BLOCK_MODES[mode] });
    }
  }

  protected setBodyMode(mode: GroupMode): void {
    if (mode !== this.bodyMode()) {
      const association = this.association();
      this.associationChange.emit({
        ...association,
        filter: { ...association.filter, ...GROUP_MODES[mode] }
      });
    }
  }
}
